import type { GaVueComponent } from "@/common/vueUtils";
import { NoResults } from "@/components/sorting-filtering-paging/NoResults";
import { contentTypeOrder, type ContentTypeProps, type SearchStoreState } from "@/components/search/fullSearchStoreTypes";
import { ContentType } from "@/components/search/ContentType";
import type { Store } from "@/common/storeUtils";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import type { IxStatusHandler } from "@/common/ixStatusUtils";
import type { FullSearchContext } from "@generated-widgets/widgets/FullSearch";

function reset(store: Store<ContentTypeProps>): void {
  const resettedFilter = store.get().filterProps.formFilters.map((filter) => ({ ...filter, activeValues: [] }));
  store.set({ ...store.get(), filterProps: { ...store.get().filterProps, formFilters: resettedFilter } });
}

export function resetFilter(store: Store<ContentTypeProps>, search: () => void): void {
  reset(store);
  search();
}

export function resetAllFilters(store: Store<ContentTypeProps[]>, search: () => void): void {
  store.get().forEach((ct) => {
    reset(store.sub(store.get().indexOf(ct)));
  });
  search();
}

function ErrorState(): GaVueComponent {
  const { t } = useGaContext();
  return (
    <div>
      <h2 class="h3">{t("search:page.search.errorTitle")}</h2>
      <p>{t("search:page.search.errorDescription")}</p>
    </div>
  );
}

export function SearchResults(props: { searchStore: Store<SearchStoreState>; search: (searchTerm: string, softReload: boolean) => void; ctx: FullSearchContext; bookmarkHandler: IxStatusHandler }): GaVueComponent {
  const contentTypeProps = props.searchStore.sub("contentTypeProps");
  const searchProps = props.searchStore.sub("searchProps");
  const filteredContentTypes = contentTypeProps.get().filter((value) => searchProps.sub("selectedContentType").get() === "ALL" || searchProps.sub("selectedContentType").get() === value.kind);

  if (searchProps.sub("status").get() === "ERROR") {
    return <ErrorState />;
  }
  return (
    <>
      {contentTypeProps.get().some((ct) => ct.items.length > 0) || searchProps.sub("status").get() === "LOADING" ? (
        <>
          {filteredContentTypes
            .sort((a, b) => contentTypeOrder[a.kind] - contentTypeOrder[b.kind])
            .map((contentType) => (
              <div class="mb-4">
                <ContentType
                  contentType={contentTypeProps.sub(contentTypeProps.get().findIndex((c) => c.kind === contentType.kind))}
                  searchProps={searchProps}
                  search={props.search}
                  resetAllFilters={() => resetAllFilters(contentTypeProps, () => props.search(searchProps.get().searchTerm, false))}
                  bookmarkHandler={props.bookmarkHandler}
                  ctx={props.ctx}
                />
              </div>
            ))}
        </>
      ) : (
        <div class="py-3">
          <NoResults
            resetFilters={() =>
              searchProps.sub("selectedContentType").get() === "ALL"
                ? resetAllFilters(contentTypeProps, () => props.search(searchProps.get().searchTerm, false))
                : resetFilter(contentTypeProps.sub(contentTypeProps.get().findIndex((ct) => ct.kind === searchProps.sub("selectedContentType").get())), () => props.search(searchProps.get().searchTerm, false))
            }
          />
        </div>
      )}
    </>
  );
}
